import ReactGA from "react-ga4";

import React, { useState, useMemo, useEffect } from 'react';
import { DollarSign, TrendingUp, Calculator, ChevronDown, ChevronUp } from 'lucide-react';
const INIT_COGS = 0.4
const INIT_SOFTWARE_FEE = 25;
const INIT_CC_FEE = 0.06;
const INIT_AI_FEE = 15;
const INIT_SALES_TAX = 0.07;
export default function ProfitCalculator() {
  const [locations, setLocations] = useState([
    { id: 1, name: 'Location 1', equipmentCost: 3500, monthlySales: 1200, cogs: INIT_COGS, softwareFee: INIT_SOFTWARE_FEE, ccProcessingFee: INIT_CC_FEE, aiFee: INIT_AI_FEE, salesTax: INIT_SALES_TAX, expanded: true }
  ]);

  useEffect(()=>{
    ReactGA.send({ hitType: "pageview", page: "/", title: "Vending Machine Profit Calculator" });

  }, [])
  
  const [loanRate, setLoanRate] = useState(7.5);
  const [loanTerm, setLoanTerm] = useState(36);
  const [fixedCosts, setFixedCosts] = useState({
    insurance: 50,
    phone: 20,
    software: 30,
    other: 50
  });
  
  const [forecastMonths, setForecastMonths] = useState(12);

  const addLocation = () => {
    setLocations([...locations, {
      id: locations.length + 1,
      name: `Location ${locations.length + 1}`,
      equipmentCost: 3500,
      monthlySales: 1200,
      cogs: INIT_COGS,
      softwareFee: INIT_SOFTWARE_FEE,
      ccProcessingFee: INIT_CC_FEE,
      aiFee: INIT_AI_FEE,
      salesTax: INIT_SALES_TAX,
      expanded: true
    }]);
  };

  const updateLocation = (id:any, field:any, value:any) => {
    setLocations(locations.map(loc => 
      loc.id === id ? { ...loc, [field]: value } : loc
    ));
  };

  const removeLocation = (id:any) => {
    if (locations.length > 1) {
      setLocations(locations.filter(loc => loc.id !== id));
    }
  };

  const toggleLocation = (id:any) => {
    setLocations(locations.map(loc => 
      loc.id === id ? { ...loc, expanded: !loc.expanded } : loc
    ));
  };

  const calculations = useMemo(() => {
    const totalEquipmentCost = locations.reduce((sum, loc) => sum + Number(loc.equipmentCost), 0);
    const monthlyRate = (loanRate / 100) / 12;
    const monthlyPayment = totalEquipmentCost > 0 && monthlyRate > 0
      ? totalEquipmentCost * (monthlyRate * Math.pow(1 + monthlyRate, loanTerm)) / (Math.pow(1 + monthlyRate, loanTerm) - 1)
      : 0;
    
    const totalFixedCosts = Object.values(fixedCosts).reduce((sum, cost) => sum + Number(cost), 0);
    
    const monthlyRevenue = locations.reduce((sum, loc) => sum + Number(loc.monthlySales), 0);
    const monthlyCOGS = locations.reduce((sum, loc) => sum + (Number(loc.monthlySales) * Number(loc.cogs)), 0);
    const monthlySoftwareFees = locations.reduce((sum, loc) => sum + Number(loc.softwareFee), 0);
    const monthlyCCFees = locations.reduce((sum, loc) => sum + (Number(loc.monthlySales) * Number(loc.ccProcessingFee)), 0);
    const monthlyAIFees = locations.reduce((sum, loc) => sum + Number(loc.aiFee), 0);
    const monthlySalesTax = locations.reduce((sum, loc) => sum + (Number(loc.monthlySales) * Number(loc.salesTax)), 0);
    const grossProfit = monthlyRevenue - monthlyCOGS - monthlySoftwareFees - monthlyCCFees - monthlyAIFees - monthlySalesTax;
    const monthlyProfit = grossProfit - totalFixedCosts - monthlyPayment;
    
    // Forecast
    let cumulativeProfit = 0;
    const forecast = [];
    for (let month = 1; month <= forecastMonths; month++) {
      const revenue = monthlyRevenue * month;
      const cogs = monthlyCOGS * month;
      const softwareFees = monthlySoftwareFees * month;
      const ccFees = monthlyCCFees * month;
      const aiFees = monthlyAIFees * month;
      const salesTax = monthlySalesTax * month;
      const fixed = totalFixedCosts * month;
      const loanPayments = month <= loanTerm ? monthlyPayment * month : monthlyPayment * loanTerm;
      const profit = revenue - cogs - softwareFees - ccFees - aiFees - salesTax - fixed - loanPayments;
      cumulativeProfit = profit;
      
      forecast.push({
        month,
        revenue,
        cogs,
        softwareFees,
        ccFees,
        aiFees,
        salesTax,
        grossProfit: revenue - cogs - softwareFees - ccFees - aiFees - salesTax,
        fixed,
        loanPayments,
        profit,
        breakeven: profit >= 0
      });
    }
    
    return {
      totalEquipmentCost,
      monthlyPayment,
      totalFixedCosts,
      monthlyRevenue,
      monthlyCOGS,
      monthlySoftwareFees,
      monthlyCCFees,
      monthlyAIFees,
      monthlySalesTax,
      grossProfit,
      grossMargin: monthlyRevenue > 0 ? ((grossProfit / monthlyRevenue) * 100) : 0,
      monthlyProfit,
      forecast
    };
  }, [locations, loanRate, loanTerm, fixedCosts, forecastMonths]);

  return (
    <div className="min-h-screen bg-black p-6">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <Calculator className="text-indigo-600" size={32} />
            <h1 className="text-3xl font-bold text-gray-800">Vending Machine Profit Calculator</h1>
          </div>
<p className="p-7">Use our free vending machine profit calculator to discover the gross profit, profit margins, and ROI you can be earning from your vending machines.</p>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-lg border border-green-200">
              <div className="text-green-600 text-sm font-semibold mb-1">Monthly Revenue</div>
              <div className="text-2xl font-bold text-green-700">${calculations.monthlyRevenue.toFixed(0)}</div>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-lg border border-blue-200">
              <div className="text-blue-600 text-sm font-semibold mb-1">Gross Profit</div>
              <div className="text-2xl font-bold text-blue-700">${calculations.grossProfit.toFixed(0)}</div>
              <div className="text-xs text-blue-600">{calculations.grossMargin.toFixed(1)}% margin</div>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-lg border border-purple-200">
              <div className="text-purple-600 text-sm font-semibold mb-1">Monthly Profit</div>
              <div className={`text-2xl font-bold ${calculations.monthlyProfit >= 0 ? 'text-purple-700' : 'text-red-600'}`}>
                ${calculations.monthlyProfit.toFixed(0)}
              </div>
            </div>
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded-lg border border-orange-200">
              <div className="text-orange-600 text-sm font-semibold mb-1">Loan Payment</div>
              <div className="text-2xl font-bold text-orange-700">${calculations.monthlyPayment.toFixed(0)}</div>
            </div>
            <div className="bg-gradient-to-br from-red-50 to-red-100 p-4 rounded-lg border border-red-200">
              <div className="text-red-600 text-sm font-semibold mb-1">CC Processing Fees</div>
              <div className="text-2xl font-bold text-red-700">${calculations.monthlyCCFees.toFixed(0)}</div>
            </div>
            <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 p-4 rounded-lg border border-indigo-200">
              <div className="text-indigo-600 text-sm font-semibold mb-1">AI Fees</div>
              <div className="text-2xl font-bold text-indigo-700">${calculations.monthlyAIFees.toFixed(0)}</div>
            </div>
            <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 p-4 rounded-lg border border-yellow-200">
              <div className="text-yellow-700 text-sm font-semibold mb-1">Sales Tax</div>
              <div className="text-2xl font-bold text-yellow-800">${calculations.monthlySalesTax.toFixed(0)}</div>
            </div>
          </div>

          {/* Locations */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-800">Locations</h2>
              <button
                onClick={addLocation}
                className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition"
              >
                + Add Location
              </button>
            </div>
            <div className="space-y-4">
              {locations.map(loc => (
                <div key={loc.id} className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
                  <div 
                    className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-100 transition"
                    onClick={() => toggleLocation(loc.id)}
                  >
                    <div className="flex items-center gap-4">
                      <h3 className="font-semibold text-gray-800">{loc.name}</h3>
                      <div className="text-sm text-gray-600">
                        Sales: ${loc.monthlySales}/mo | Equipment: ${loc.equipmentCost}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {loc.expanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </div>
                  </div>
                  
                  {loc.expanded && (
                    <div className="p-4 border-t border-gray-200 bg-white">
                      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                          <input
                            type="text"
                            value={loc.name}
                            onChange={(e) => updateLocation(loc.id, 'name', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Equipment Cost</label>
                          <input
                            type="number"
                            value={loc.equipmentCost}
                            onChange={(e) => updateLocation(loc.id, 'equipmentCost', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Sales</label>
                          <input
                            type="number"
                            value={loc.monthlySales}
                            onChange={(e) => updateLocation(loc.id, 'monthlySales', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">COGS %</label>
                          <input
                            type="number"
                            step="0.01"
                            value={loc.cogs}
                            onChange={(e) => updateLocation(loc.id, 'cogs', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Software Fee</label>
                          <input
                            type="number"
                            value={loc.softwareFee}
                            onChange={(e) => updateLocation(loc.id, 'softwareFee', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">CC Fee %</label>
                          <input
                            type="number"
                            step="0.001"
                            value={loc.ccProcessingFee}
                            onChange={(e) => updateLocation(loc.id, 'ccProcessingFee', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">AI Fee</label>
                          <input
                            type="number"
                            value={loc.aiFee}
                            onChange={(e) => updateLocation(loc.id, 'aiFee', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Sales Tax %</label>
                          <input
                            type="number"
                            step="0.001"
                            value={loc.salesTax}
                            onChange={(e) => updateLocation(loc.id, 'salesTax', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                          />
                        </div>
                      </div>
                      <div className="mt-4">
                        <button
                          onClick={() => removeLocation(loc.id)}
                          className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition disabled:opacity-50"
                          disabled={locations.length === 1}
                        >
                          Remove Location
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Loan & Fixed Costs */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Loan Details</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Total Equipment Cost</label>
                  <div className="text-2xl font-bold text-gray-800">${calculations.totalEquipmentCost.toFixed(0)}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Interest Rate (%)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={loanRate}
                    onChange={(e) => setLoanRate(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Loan Term (months)</label>
                  <input
                    type="number"
                    value={loanTerm}
                    onChange={(e) => setLoanTerm(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Fixed Monthly Costs</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Insurance</label>
                  <input
                    type="number"
                    value={fixedCosts.insurance}
                    onChange={(e) => setFixedCosts({...fixedCosts, insurance: Number(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="number"
                    value={fixedCosts.phone}
                    onChange={(e) => setFixedCosts({...fixedCosts, phone: Number(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Software</label>
                  <input
                    type="number"
                    value={fixedCosts.software}
                    onChange={(e) => setFixedCosts({...fixedCosts, software: Number(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Other</label>
                  <input
                    type="number"
                    value={fixedCosts.other}
                    onChange={(e) => setFixedCosts({...fixedCosts, other: Number(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div className="pt-2 border-t border-gray-300">
                  <div className="text-sm font-medium text-gray-700">Total Fixed Costs</div>
                  <div className="text-xl font-bold text-gray-800">${calculations.totalFixedCosts.toFixed(0)}/mo</div>
                </div>
              </div>
            </div>
          </div>

          {/* Forecast */}
          <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-800">Financial Forecast</h3>
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">Months:</label>
                <input
                  type="number"
                  value={forecastMonths}
                  onChange={(e) => setForecastMonths(Number(e.target.value))}
                  className="w-20 px-3 py-2 border border-gray-300 rounded-lg"
                  min="1"
                  max="60"
                />
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-200">
                  <tr>
                    <th className="px-3 py-2 text-left">Month</th>
                    <th className="px-3 py-2 text-right">Revenue</th>
                    <th className="px-3 py-2 text-right">COGS</th>
                    <th className="px-3 py-2 text-right">Software</th>
                    <th className="px-3 py-2 text-right">CC Fees</th>
                    <th className="px-3 py-2 text-right">AI Fees</th>
                    <th className="px-3 py-2 text-right">Sales Tax</th>
                    <th className="px-3 py-2 text-right">Gross Profit</th>
                    <th className="px-3 py-2 text-right">Fixed Costs</th>
                    <th className="px-3 py-2 text-right">Loan Payments</th>
                    <th className="px-3 py-2 text-right">Net Profit</th>
                  </tr>
                </thead>
                <tbody>
                  {calculations.forecast.map(f => (
                    <tr key={f.month} className={`border-b border-gray-200 ${f.breakeven ? 'bg-green-50' : ''}`}>
                      <td className="px-3 py-2">{f.month}</td>
                      <td className="px-3 py-2 text-right">${f.revenue.toFixed(0)}</td>
                      <td className="px-3 py-2 text-right">${f.cogs.toFixed(0)}</td>
                      <td className="px-3 py-2 text-right">${f.softwareFees.toFixed(0)}</td>
                      <td className="px-3 py-2 text-right">${f.ccFees.toFixed(0)}</td>
                      <td className="px-3 py-2 text-right">${f.aiFees.toFixed(0)}</td>
                      <td className="px-3 py-2 text-right">${f.salesTax.toFixed(0)}</td>
                      <td className="px-3 py-2 text-right">${f.grossProfit.toFixed(0)}</td>
                      <td className="px-3 py-2 text-right">${f.fixed.toFixed(0)}</td>
                      <td className="px-3 py-2 text-right">${f.loanPayments.toFixed(0)}</td>
                      <td className={`px-3 py-2 text-right font-semibold ${f.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        ${f.profit.toFixed(0)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
     <footer className="text-white">Powered by <a  href="http://www.snacktothefuturevending.com">Snack to the Future, LLC</a></footer>
    </div>
    
  );
}